import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Printer, RefreshCw, Plus, Trash2, CheckCircle2, Lock, Shield, DollarSign, AlertTriangle } from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { API } from "@/config";

const Money = ({ v }) => <span>${Number(v || 0).toFixed(2)}</span>;

/* -------------------------------------------------------------------------- */
/* Federal Tax Savings Reserve                                                 */
/* -------------------------------------------------------------------------- */
const FederalTaxSavings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [period, setPeriod] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/federal-tax-savings`);
      setData(d);
    } catch { toast.error("Could not load federal tax savings data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recordPayment = async () => {
    if (!amount) { toast.error("Amount required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/federal-tax-savings/record-payment`, {
        amount: Number(amount), payment_date: payDate, period, reference, notes,
      });
      toast.success("Tax payment recorded");
      setFormOpen(false); setAmount(""); setPeriod(""); setReference(""); setNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const removePayment = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await axios.delete(`${API}/admin/federal-tax-savings/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <Card className="p-5"><p className="text-[#B9C8D5]">Loading federal tax savings…</p></Card>;
  if (!data) return null;

  const pct = Math.round((data.federal_rate || 0.25) * 100);
  const overPaid = data.total_paid_to_irs > data.total_required;

  return (
    <Card data-testid="federal-tax-savings">
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-300" />
            <h2 className="font-serif text-xl">Federal Tax Savings Reserve</h2>
          </div>
          <p className="text-xs text-[#B9C8D5] max-w-sm">
            Allocation tracking only — this is your savings target, not your actual tax liability or a deductible expense.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: `Required (${pct}% of revenue)`, v: data.total_required, color: "text-orange-300" },
            { label: "Already paid (IRS/EFTPS)", v: data.total_paid_to_irs, color: "text-emerald-300" },
            { label: "Net balance to save", v: data.net_balance, color: data.net_balance < 0 ? "text-emerald-300" : "text-amber-300" },
            { label: "Reservations counted", v: data.reservation_count, raw: true, color: "text-[#F6F1E4]" },
          ].map((c) => (
            <div key={c.label} className="bg-black/20 rounded-xl p-3 border border-[#FFD166]/10">
              <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-1">{c.label}</div>
              <div className={`font-serif text-xl ${c.color}`} data-testid={`fed-savings-${c.label}`}>
                {c.raw ? c.v : <Money v={c.v} />}
              </div>
            </div>
          ))}
        </div>

        {overPaid && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-sm text-emerald-300">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            You've recorded more in IRS payments than the current required reserve. No additional savings needed based on current data.
          </div>
        )}

        {/* Payment history */}
        {data.payments?.length > 0 && (
          <div>
            <h3 className="text-[11px] uppercase tracking-wider text-[#B9C8D5] mb-2">Recorded tax payments</h3>
            <div className="space-y-1">
              {data.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg border border-[#FFD166]/10 bg-black/10">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-[#F6F1E4] font-medium">${Number(p.amount).toFixed(2)}</span>
                    <span className="text-xs text-[#B9C8D5] ml-2">{p.payment_date}</span>
                    {p.period && <span className="text-xs text-[#B9C8D5] ml-2">· {p.period}</span>}
                    {p.reference && <span className="text-xs text-[#B9C8D5]/60 ml-2 font-mono">{p.reference}</span>}
                    {p.notes && <p className="text-xs text-[#B9C8D5]/60 mt-0.5 italic">{p.notes}</p>}
                  </div>
                  <button onClick={() => removePayment(p.id)} className="ml-3 text-red-400/60 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Record payment form */}
        {formOpen ? (
          <div className="space-y-3 p-4 rounded-xl border border-orange-500/20 bg-orange-500/5">
            <h3 className="text-sm font-medium text-orange-300">Record estimated tax payment</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="text-[#B9C8D5] block mb-1">Amount paid ($)</span>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" />
              </label>
              <label className="text-xs">
                <span className="text-[#B9C8D5] block mb-1">Payment date</span>
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" />
              </label>
              <label className="text-xs">
                <span className="text-[#B9C8D5] block mb-1">Period (e.g. Q1 2026)</span>
                <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Q1 2026" className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" />
              </label>
              <label className="text-xs">
                <span className="text-[#B9C8D5] block mb-1">IRS/EFTPS reference</span>
                <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Confirmation #" className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" />
              </label>
            </div>
            <label className="block text-xs">
              <span className="text-[#B9C8D5] block mb-1">Notes (optional)</span>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Q1 2026 estimated payment" className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" />
            </label>
            <p className="text-[10px] text-[#B9C8D5]/60 italic">Recording a payment does not remit taxes or move funds. It only updates this tracking ledger.</p>
            <div className="flex gap-2">
              <Btn onClick={recordPayment} disabled={saving}>{saving ? "Saving…" : "Save payment"}</Btn>
              <Btn variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Btn>
            </div>
          </div>
        ) : (
          <Btn variant="ghost" onClick={() => setFormOpen(true)} data-testid="add-tax-payment-btn">
            <Plus className="w-4 h-4" /> Record IRS/EFTPS payment
          </Btn>
        )}
      </div>
    </Card>
  );
};

/* -------------------------------------------------------------------------- */
/* Master Reserve Release (Item 10)                                            */
/* -------------------------------------------------------------------------- */
const RESERVE_CATEGORIES = [
  { key: "sales_tax", label: "Sales Tax (FL DOR)" },
  { key: "federal_reserve", label: "Federal Tax Savings" },
  { key: "affiliate_commission", label: "Affiliate Commission" },
  { key: "other", label: "Other" },
];

const MasterReserveRelease = () => {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // Step 1: re-auth; Step 2: details + confirm
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [reauthToken, setReauthToken] = useState("");
  const [reauthBusy, setReauthBusy] = useState(false);
  const [category, setCategory] = useState("federal_reserve");
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState("");
  const [reason, setReason] = useState("");
  const [releasing, setReleasing] = useState(false);

  const loadLog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reserve-release`);
      setLog(data.records || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const reAuth = async () => {
    if (!password) { toast.error("Password required"); return; }
    setReauthBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/auth/re-verify`, { password });
      setReauthToken(data.reauth_token);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Incorrect password"); }
    finally { setReauthBusy(false); }
  };

  const releaseReserve = async () => {
    if (!amount || !destination || !reason) { toast.error("Amount, destination, and reason are required"); return; }
    setReleasing(true);
    try {
      await axios.post(`${API}/admin/reserve-release`, {
        reauth_token: reauthToken, category, amount: Number(amount), destination, reason,
      });
      toast.success("Reserve release recorded in audit log");
      setOpen(false); setStep(1); setPassword(""); setReauthToken(""); setAmount(""); setDestination(""); setReason("");
      loadLog();
    } catch (e) { toast.error(e.response?.data?.detail || "Release failed"); }
    finally { setReleasing(false); }
  };

  return (
    <Card data-testid="master-reserve-release">
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-red-400" />
            <h2 className="font-serif text-xl">Release Reserved Funds</h2>
          </div>
          <p className="text-xs text-[#B9C8D5] max-w-md">
            Admin-only. Records a reserve release in the audit log. Does not automatically move funds or mark taxes as remitted.
          </p>
        </div>

        {!open ? (
          <Btn variant="danger" onClick={() => setOpen(true)} data-testid="open-reserve-release-btn">
            <Lock className="w-4 h-4" /> Release reserved funds
          </Btn>
        ) : (
          <div className="space-y-4 p-4 rounded-xl border border-red-500/20 bg-red-500/5">
            {step === 1 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-red-300">
                  <Lock className="w-4 h-4" />
                  <span className="font-medium">Re-authenticate to continue</span>
                </div>
                <p className="text-xs text-[#B9C8D5]">This action is logged. Enter your admin password to proceed.</p>
                <label className="block text-xs">
                  <span className="text-[#B9C8D5] block mb-1">Admin password</span>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && reAuth()}
                    className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" placeholder="Your password" autoFocus />
                </label>
                <div className="flex gap-2">
                  <Btn onClick={reAuth} disabled={reauthBusy}>{reauthBusy ? "Verifying…" : "Verify identity"}</Btn>
                  <Btn variant="ghost" onClick={() => { setOpen(false); setStep(1); setPassword(""); }}>Cancel</Btn>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-emerald-300">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Identity verified · 15-minute window</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs">
                    <span className="text-[#B9C8D5] block mb-1">Reserve category</span>
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm">
                      {RESERVE_CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs">
                    <span className="text-[#B9C8D5] block mb-1">Amount ($)</span>
                    <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" placeholder="0.00" />
                  </label>
                  <label className="text-xs col-span-2">
                    <span className="text-[#B9C8D5] block mb-1">Destination</span>
                    <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" placeholder="e.g. Business bank account ending in 4321" />
                  </label>
                  <label className="text-xs col-span-2">
                    <span className="text-[#B9C8D5] block mb-1">Reason</span>
                    <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm" placeholder="e.g. Q1 2026 estimated tax payment" />
                  </label>
                </div>
                <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 text-xs text-amber-300 space-y-1">
                  <p className="font-medium">Before confirming:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-[#B9C8D5]">
                    <li>This creates an audit record only — it does NOT automatically transfer funds</li>
                    <li>Releasing sales tax does not mark it as remitted to FL DOR</li>
                    <li>Releasing affiliate reserves does not mark commissions as paid</li>
                    <li>Actual fund movement is a separate Stripe action</li>
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Btn variant="danger" onClick={releaseReserve} disabled={releasing} data-testid="confirm-reserve-release-btn">
                    {releasing ? "Recording…" : "Record reserve release"}
                  </Btn>
                  <Btn variant="ghost" onClick={() => { setOpen(false); setStep(1); setPassword(""); setReauthToken(""); }}>Cancel</Btn>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Audit log */}
        {log.length > 0 && (
          <div>
            <h3 className="text-[11px] uppercase tracking-wider text-[#B9C8D5] mb-2">Audit log</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {log.map((r) => (
                <div key={r.id} className="flex items-start justify-between p-2.5 rounded-lg border border-[#FFD166]/10 bg-black/10 text-xs">
                  <div>
                    <span className="text-[#F6F1E4] font-medium">{RESERVE_CATEGORIES.find((c) => c.key === r.category)?.label || r.category}</span>
                    <span className="ml-2 text-emerald-300 font-mono">${Number(r.amount).toFixed(2)}</span>
                    <span className="ml-2 text-[#B9C8D5]/60">→ {r.destination}</span>
                    <p className="text-[#B9C8D5]/70 mt-0.5 italic">{r.reason}</p>
                  </div>
                  <div className="text-right text-[#B9C8D5]/50 shrink-0 ml-4">
                    <p>{r.at?.slice(0, 10)}</p>
                    <p>{r.performed_by}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};


/* -------------------------------------------------------------------------- */
/* Tax Remittances sub-section                                                 */
/* -------------------------------------------------------------------------- */
const TaxRemittances = ({ unremittedTotal }) => {
  const [remits, setRemits] = useState([]);
  const [totalRemitted, setTotalRemitted] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [remittedAt, setRemittedAt] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tax-remittances`);
      setRemits(data.remittances || []);
      setTotalRemitted(data.total_remitted || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!amount || !remittedAt) { toast.error("Amount and remitted date are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tax-remittances`, {
        amount: Number(amount),
        remitted_at: remittedAt,
        period_start: periodStart,
        period_end: periodEnd,
        notes,
      });
      toast.success("Remittance recorded");
      setFormOpen(false); setAmount(""); setRemittedAt(""); setPeriodStart(""); setPeriodEnd(""); setNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this remittance record? The sales tax reserve will increase accordingly.")) return;
    try {
      await axios.delete(`${API}/admin/tax-remittances/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const stillOwed = Math.max(0, (unremittedTotal || 0) - totalRemitted);

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <h2 className="font-serif text-xl text-[#FFD166]">FL DOR Tax Remittances</h2>
          </div>
          <Btn variant="ghost" onClick={() => setFormOpen((o) => !o)} data-testid="tax-remit-add-btn">
            <Plus className="w-3.5 h-3.5" /> Record remittance
          </Btn>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          Record each DR-15 filing/payment here. The Payouts page uses this list to calculate the outstanding Sales Tax Reserve (collected but not yet filed with the FL DOR).
        </p>

        {/* Summary */}
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">Outstanding liability (all-time)</div>
            <div className="font-serif text-xl text-orange-300 mt-1">${Number(unremittedTotal || 0).toFixed(2)}</div>
          </div>
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">Total remitted</div>
            <div className="font-serif text-xl text-emerald-300 mt-1">${Number(totalRemitted).toFixed(2)}</div>
          </div>
          <div className={`rounded-lg border p-3 ${stillOwed > 0 ? "border-red-500/30 bg-red-500/5" : "border-emerald-500/20 bg-emerald-500/5"}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">Still owed (reserve)</div>
            <div className={`font-serif text-xl mt-1 ${stillOwed > 0 ? "text-red-300" : "text-emerald-300"}`}>${stillOwed.toFixed(2)}</div>
          </div>
        </div>

        {/* Add form */}
        {formOpen && (
          <div className="rounded-xl border border-[#FFD166]/20 bg-black/30 p-4 mb-4 space-y-3" data-testid="tax-remit-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="eyebrow block mb-1">Amount paid to FL DOR ($)</label>
                <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1250.00" data-testid="tax-remit-amount" />
              </div>
              <div>
                <label className="eyebrow block mb-1">Date paid / remitted</label>
                <Input type="date" value={remittedAt} onChange={(e) => setRemittedAt(e.target.value)} data-testid="tax-remit-date" />
              </div>
              <div>
                <label className="eyebrow block mb-1">Period start (optional)</label>
                <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Period end (optional)</label>
                <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1">Notes (optional)</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Q1 2026 DR-15 filing" data-testid="tax-remit-notes" />
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Btn>
              <Btn onClick={submit} disabled={saving} data-testid="tax-remit-submit">{saving ? "Saving…" : "Save remittance"}</Btn>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <p className="text-[#B9C8D5] text-sm">Loading…</p>
        ) : remits.length === 0 ? (
          <p className="text-[#B9C8D5] text-sm italic">No remittances recorded yet. Add one above when you file with the FL DOR.</p>
        ) : (
          <div className="space-y-2" data-testid="tax-remit-list">
            {remits.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-[#FFD166]/10 bg-black/20 p-3" data-testid={`tax-remit-row-${r.id}`}>
                <div>
                  <span className="font-serif text-lg text-emerald-300">${Number(r.amount).toFixed(2)}</span>
                  <span className="text-xs text-[#B9C8D5] ml-3">paid {r.remitted_at}</span>
                  {r.period_start && <span className="text-xs text-[#B9C8D5] ml-2">· period {r.period_start} → {r.period_end}</span>}
                  {r.notes && <div className="text-[11px] text-[#B9C8D5] italic">{r.notes}</div>}
                  <div className="text-[10px] text-[#B9C8D5]/60">recorded by {r.created_by}</div>
                </div>
                <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300 p-1.5 rounded-lg hover:bg-red-500/10" data-testid={`tax-remit-delete-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};


/* -------------------------------------------------------------------------- */
/* Main Tax Report page                                                         */
/* -------------------------------------------------------------------------- */
export const TaxReport = () => {
  const today = new Date();
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 24 * 3600 * 1000);
  const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(iso(lastMonthStart));
  const [end, setEnd] = useState(iso(lastMonthEnd));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  // Aggregate collected tax (all-time) for remittances section
  const [allTimeTaxCollected, setAllTimeTaxCollected] = useState(0);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tax-report`, { params: { start, end } });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report failed");
    } finally { setLoading(false); }
  }, [start, end]);

  // Fetch all-time tax liability from the unified endpoint (no date range needed)
  const fetchAllTime = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tax-liability-summary`);
      setAllTimeTaxCollected(data.outstanding_liability || 0);
    } catch { /* ignore */ }
  }, []); // eslint-disable-line

  useEffect(() => {
    run();
    fetchAllTime();
  // eslint-disable-next-line
  }, []);

  const downloadCsv = () => {
    if (!report) return;
    const rows = [
      ["Reservation ID", "Delivery date", "Customer name", "Customer email", "Destination", "Taxable sales ($)", "Delivery fee ($, non-taxable)", "Sales tax collected ($)", "Total ($)"],
      ...(report.reservations || []).map((r) => [
        r.id,
        r.delivery_date,
        (r.customer_name || "").replace(/,/g, " "),
        r.customer_email,
        (r.delivery_resort || "").replace(/,/g, " "),
        Number(r.subtotal || 0).toFixed(2),
        Number(r.delivery_fee || 0).toFixed(2),
        Number(r.tax || 0).toFixed(2),
        Number(r.total || 0).toFixed(2),
      ]),
      [],
      ["TOTALS", "", "", "", "",
        report.taxable_sales.toFixed(2),
        report.exempt_delivery_fees.toFixed(2),
        report.sales_tax_collected.toFixed(2),
        report.gross_receipts.toFixed(2)],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `dr-15_${start}_to_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const fmtD = (d) => d?.toISOString()?.slice(0, 10);
  return (
    <div className="max-w-6xl mx-auto px-5 py-8 space-y-6" data-testid="tax-report-page">
      <div className="flex items-center gap-3">
        <FileText className="w-6 h-6 text-[#FFD166]" />
        <h1 className="font-serif text-3xl">Florida DR-15 Tax Report</h1>
      </div>
      <p className="text-sm text-[#B9C8D5] max-w-2xl">
        Monthly Florida Sales &amp; Use Tax filing (DR-15). Numbers below match what you'll enter on the state form: taxable sales, exempt delivery, tax collected, and the state/county surtax split (6% + 1.5% Osceola). Delivery fees are separately-stated and NOT taxable in Florida.
      </p>

      <Card>
        <div className="p-5">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="eyebrow block mb-1">Period start</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input" data-testid="tax-start" />
            </label>
            <label className="text-sm">
              <span className="eyebrow block mb-1">Period end</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input" data-testid="tax-end" />
            </label>
            <Btn onClick={run} disabled={loading} data-testid="tax-run"><RefreshCw className="w-4 h-4" /> {loading ? "Running…" : "Run report"}</Btn>
            {report && <Btn variant="ghost" onClick={downloadCsv} data-testid="tax-download"><Printer className="w-4 h-4" /> Download CSV</Btn>}
          </div>
          <p className="text-[11px] text-[#B9C8D5] mt-2">Tip: for monthly filings, keep the default (previous calendar month). For quarterly filings, expand the range.</p>
        </div>
      </Card>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tax-summary">
            {[
              { label: "Gross receipts", key: "gross_receipts", tone: "text-[#FFD166]" },
              { label: "Taxable sales", key: "taxable_sales" },
              { label: "Exempt delivery fees", key: "exempt_delivery_fees" },
              { label: "Sales tax collected", key: "sales_tax_collected", tone: "text-[#7EE787]" },
              { label: "State tax (6%)", key: "state_tax_6pct" },
              { label: "County surtax (1.5%)", key: "county_surtax_1_5pct" },
              { label: "Reservations", key: "reservation_count", raw: true },
              { label: "Period", value: `${report.period.start} → ${report.period.end}`, raw: true },
            ].map((c) => (
              <Card key={c.label} className="p-4">
                <div className="text-[11px] uppercase tracking-wider text-[#B9C8D5]">{c.label}</div>
                <div className={`font-serif text-2xl mt-1 ${c.tone || "text-[#F6F1E4]"}`} data-testid={`tax-stat-${c.key || c.label}`}>
                  {c.raw ? (c.value ?? report[c.key]) : <Money v={report[c.key]} />}
                </div>
              </Card>
            ))}
          </div>

          <Card>
            <div className="p-5">
              <h2 className="font-serif text-xl mb-4">Per-reservation breakdown</h2>
              {(report.reservations || []).length === 0 ? (
                <p className="text-[#B9C8D5]">No paid deliveries in this window.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tax-rows">
                    <thead className="text-left text-[#B9C8D5]">
                      <tr>
                        <th className="py-2 pr-4">Delivery</th>
                        <th className="py-2 pr-4">Customer</th>
                        <th className="py-2 pr-4">Destination</th>
                        <th className="py-2 pr-4 text-right">Taxable</th>
                        <th className="py-2 pr-4 text-right">Delivery</th>
                        <th className="py-2 pr-4 text-right">Tax</th>
                        <th className="py-2 pr-4 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.reservations.map((r) => (
                        <tr key={r.id} className="border-t border-[#FFD166]/10">
                          <td className="py-2 pr-4">{r.delivery_date}</td>
                          <td className="py-2 pr-4">{r.customer_name}<div className="text-[11px] text-[#B9C8D5]">{r.customer_email}</div></td>
                          <td className="py-2 pr-4">{r.delivery_resort}</td>
                          <td className="py-2 pr-4 text-right"><Money v={r.subtotal} /></td>
                          <td className="py-2 pr-4 text-right text-[#B9C8D5]"><Money v={r.delivery_fee} /></td>
                          <td className="py-2 pr-4 text-right text-[#7EE787]"><Money v={r.tax} /></td>
                          <td className="py-2 pr-4 text-right"><Money v={r.total} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Federal Tax Savings Reserve */}
      <FederalTaxSavings />

      {/* Master Reserve Release */}
      <MasterReserveRelease />

      {/* Tax Remittances — always visible, sources from all-time data */}
      <TaxRemittances unremittedTotal={allTimeTaxCollected} />
    </div>
  );
};
